import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from '../utils/SimpleRouter';
import axios from 'axios';
import { X } from 'lucide-react';
import { getApiUrl, getBackendUrl } from '../utils/apiUrl';

const API = getApiUrl();
const BACKEND_URL = getBackendUrl();

const MobileThankYou = () => {
  const { qrCodeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Helper to convert authenticated file URLs to public URLs
  const getPublicImageUrl = (url) => {
    if (!url) return '';
    // If it's already a full URL (CloudFront, S3, etc.), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Convert /api/files/ to /api/public-files/ for public access
    if (url.startsWith('/api/files/')) {
      return `${BACKEND_URL}${url.replace('/api/files/', '/api/public-files/')}`;
    }
    // For other relative URLs, prepend backend URL
    return `${BACKEND_URL}${url}`;
  };

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [qrCodeId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/display/${qrCodeId}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleClose = () => {
    window.close();
    // If window.close() doesn't work (blocked by browser), redirect to a thank you message
    setTimeout(() => {
      navigate('/');
    }, 100);
  };

  if (!data) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '1.2rem'
      }}>
        Laster...
      </div>
    );
  }

  return (
    <>
      {/* Global styles to prevent horizontal scroll */}
      <style>{`
        html, body {
          overflow-x: hidden !important;
          max-width: 100vw !important;
          width: 100% !important;
        }
        * {
          box-sizing: border-box;
        }
      `}</style>
      
      <div style={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        background: data.organization.dark_mode 
          ? data.organization.theme_colors?.dark_mode_bg || '#2A2A2A'
          : data.organization.theme_colors?.light_mode_bg || '#F3F3F3',
        color: data.organization.dark_mode 
          ? data.organization.theme_colors?.font_light || '#FFFFFF'
          : data.organization.theme_colors?.font_dark || '#2A2A2A',
        padding: '1rem',
        position: 'relative',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000
          }}
        >
          <X size={24} color={data.organization.theme_colors?.primary_color || '#50E3C2'} />
        </button>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem',
          paddingTop: '3rem',
          paddingLeft: 0,
          paddingRight: 0
        }}>
          <h1 style={{
            fontSize: '1.75rem',
            marginBottom: '0.5rem',
            fontWeight: 'bold',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {data.organization.name}
          </h1>
          <div style={{
            fontSize: '2rem',
            fontWeight: '700',
            fontFamily: "'Roboto Condensed', sans-serif",
            color: data.organization.theme_colors?.primary_color || '#50E3C2'
          }}>
            {currentTime.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>

        {/* Success message */}
        <div style={{
          background: 'rgba(80, 227, 194, 0.15)',
          padding: '1.25rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          textAlign: 'center',
          border: `2px solid ${data.organization.theme_colors?.primary_color || '#50E3C2'}`,
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            marginBottom: '0.5rem',
            color: data.organization.theme_colors?.primary_color || '#50E3C2'
          }}>
            ✓ Melding mottatt!
          </h2>
          <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
            Din melding er sendt til {data.screen.screen_name || 'skjermen'} og vil vises etter godkjenning.
          </p>
        </div>

        {/* Messages */}
        {data.messages.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1.25rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              Aktuelle meldinger
            </h3>
            {data.messages.map((message, idx) => (
              <div
                key={idx}
                style={{
                  background: data.organization.dark_mode 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  padding: '1rem',
                  borderRadius: '12px',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                <h4 style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  margin: '0 0 0.5rem 0'
                }}>
                  {message.title}
                </h4>
                <p style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  marginBottom: '0.5rem',
                  margin: '0 0 0.5rem 0'
                }}>
                  {message.message}
                </p>
                <div style={{
                  fontSize: '0.85rem',
                  opacity: 0.7
                }}>
                  {new Date(message.created_at).toLocaleDateString('no-NO')}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Articles */}
        {data.articles.length > 0 && (
          <div style={{
            background: `${data.organization.theme_colors?.primary_color || '#50E3C2'}15`,
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              marginBottom: '1rem',
              textAlign: 'center',
              color: data.organization.theme_colors?.primary_color || '#50E3C2'
            }}>
              {data.organization.name} informerer om
            </h3>
            {data.articles.map((article, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < data.articles.length - 1 ? '1.5rem' : '0'
                }}
              >
                {article.image_url && (
                  <img
                    src={getPublicImageUrl(article.image_url)}
                    alt={article.title}
                    onError={(e) => { e.target.style.display = 'none'; }}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      borderRadius: '8px',
                      marginBottom: '0.75rem',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                )}
                <h4 style={{
                  fontSize: '1.15rem',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  margin: '0 0 0.5rem 0'
                }}>
                  {article.title}
                </h4>
                <p style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {article.content}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Footer byline */}
        <div style={{
          textAlign: 'center',
          padding: '2rem 0',
          fontSize: '0.85rem',
          opacity: 0.6
        }}>
          <a 
            href="https://infovegg.no" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: 'inherit', 
              textDecoration: 'none',
              marginRight: '0.5rem'
            }}
          >
            INFOVEGG.no
          </a>
          - Developed by{' '}
          <a 
            href="https://displaysystem.no" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Displaysystem.no
          </a>
        </div>
      </div>
    </>
  );
};

export default MobileThankYou;
